import {
  DUE_SOON_MAX_WINDOW_MS,
  OPEN_TASK_STATUSES,
} from '@/components/TaskExpiringLabel/taskExpiration';
import type { TaskResponse, TaskStatusType } from '@/store/tasks/tasks.dto';
import { useTasksListQuery } from '@/store/tasks/tasks.queries';

/**
 * Every open maintenance job that could currently carry a due marker, and nothing else.
 *
 * A job only ever reads as overdue or due soon, and both need a deadline no further out
 * than `DUE_SOON_MAX_WINDOW_MS` — the widest the due-soon window ever opens. So
 * `to = now + that` is not an approximation: it is the complete candidate set, and a job
 * outside it cannot qualify for either state.
 *
 * The exact split is still decided in the browser, because it has to be: `isTaskDueSoon`
 * is proportional — a quarter of each job's own lifespan, floored at 30 minutes, capped
 * at 24 hours, ignoring jobs younger than an hour — so two jobs due the same afternoon
 * can disagree and no date range expresses it.
 */

/**
 * No page, rather than a page size. The filter above is what bounds this request, so the
 * response is the candidate set whatever number sits here — asking for "all of them"
 * returns exactly the rows that match and not one more. That is the difference between
 * this and the `limit=1000` it replaces: there the request carried no filter, so a
 * thousand unrelated rows really did come back.
 *
 * Sizing it from a `count` probe instead would fetch the same rows for the price of an
 * extra round trip.
 */
const ALL_MATCHING = 99999;

/**
 * The horizon is rounded up to the hour so that every caller computes the same string
 * and therefore the same query key — one request serves the Maintain queue and the
 * equipment grid on the page that shows both. It advances on its own each hour, which
 * is freshness enough for a 24-hour window, and over-reaching by under an hour costs
 * nothing: the predicates narrow it exactly.
 */
const HORIZON_BUCKET_MS = 60 * 60 * 1000;

const dueHorizon = (): string => {
  const raw = Date.now() + DUE_SOON_MAX_WINDOW_MS;
  return new Date(Math.ceil(raw / HORIZON_BUCKET_MS) * HORIZON_BUCKET_MS).toISOString();
};

const NO_TASKS: TaskResponse[] = [];

export type DueTasks = {
  /** Candidates for a due marker — overdue and imminent alike, still to be told apart. */
  tasks: TaskResponse[];
  isPending: boolean;
};

export const useDueTasks = (): DueTasks => {
  const query = useTasksListQuery(
    {
      status: OPEN_TASK_STATUSES as TaskStatusType[],
      equipment_id: 'all',
      to: dueHorizon(),
      limit: ALL_MATCHING,
    },
    1,
  );

  return {
    tasks: query.data?.items ?? NO_TASKS,
    isPending: query.isPending,
  };
};
